import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChatCircleDots, Copy, ArrowSquareOut, CheckCircle } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api, { apiErrMsg } from "../../lib/api";

export default function QuoraInbox() {
  const [rows, setRows] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/autoswarm/quora-inbox"); setRows(data || []); }
    catch (e) { toast.error(apiErrMsg(e, "Load failed")); }
  };
  useEffect(() => { load(); }, []);

  const copyAndMark = async (r) => {
    try {
      await navigator.clipboard.writeText(r.quora_answer);
      toast.success("Copied · paste into a Quora answer, then click Mark posted");
    } catch (e) { toast.error("Copy failed — select manually below"); }
  };

  const markPosted = async (id) => {
    try { await api.post(`/autoswarm/quora-inbox/${id}/mark-posted`); toast.success("Marked posted"); load(); }
    catch (e) { toast.error(apiErrMsg(e, "Mark failed")); }
  };

  const pending = rows.filter((r) => r.quora_status !== "posted");
  const posted = rows.filter((r) => r.quora_status === "posted");

  return (
    <Layout
      title="Quora Inbox"
      subtitle="AI-drafted Quora answers ready for you to paste on quora.com"
    >
      {rows.length === 0 && (
        <div className="border border-dashed border-black/15 bg-white p-16 text-center text-[#717680]" data-testid="quora-empty">
          <ChatCircleDots size={40} className="mx-auto text-[#F4A261] mb-3"/>
          <p className="text-sm">No Quora drafts yet — they&apos;ll appear here after the autoswarm scans your HOT leads.</p>
        </div>
      )}
      {pending.length > 0 && (
        <>
          <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429] mb-2">Pending · {pending.length}</div>
          <div className="space-y-4 mb-8" data-testid="quora-pending">
            {pending.map((r) => (
              <QuoraCard key={r.id} r={r} onCopy={() => copyAndMark(r)} onMark={() => markPosted(r.id)}/>
            ))}
          </div>
        </>
      )}
      {posted.length > 0 && (
        <>
          <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#0D9488] mb-2">Posted · {posted.length}</div>
          <div className="space-y-4 opacity-70" data-testid="quora-posted">
            {posted.map((r) => (<QuoraCard key={r.id} r={r} posted/>))}
          </div>
        </>
      )}
    </Layout>
  );
}

function QuoraCard({ r, onCopy, onMark, posted }) {
  return (
    <div className="border border-black/10 bg-white p-5" data-testid={`quora-card-${r.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="font-display font-black text-base tracking-tight flex-1">{r.lead_name}</div>
        <span className="px-2 py-0.5 bg-[#F5F6F8] text-[10px] font-mono-shelv uppercase">{r.region?.replace(/_/g, " ")}</span>
        <span className="px-2 py-0.5 bg-[#D90429]/10 text-[#D90429] text-[10px] font-mono-shelv uppercase">{r.product_line}</span>
        {posted && <CheckCircle size={16} weight="fill" className="text-[#0D9488]"/>}
      </div>
      <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-[#0A0A0A] border-l-2 border-[#F4A261] pl-3 my-3" data-testid={`quora-body-${r.id}`}>{r.quora_answer}</pre>
      <div className="text-[11px] text-[#717680] font-mono-shelv break-all mb-3">CTA URL: {r.target_url}</div>
      {!posted && (
        <div className="flex flex-wrap gap-2">
          <button onClick={onCopy} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F4A261] text-white text-xs font-semibold hover:bg-[#EF9748]" data-testid={`quora-copy-${r.id}`}>
            <Copy size={12}/> Copy answer
          </button>
          <a href="https://www.quora.com/answer" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-black/20 text-[#0A0A0A] text-xs font-semibold hover:bg-black hover:text-white">
            <ArrowSquareOut size={12}/> Open Quora
          </a>
          <button onClick={onMark} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0D9488] text-[#0D9488] text-xs font-semibold hover:bg-[#0D9488] hover:text-white" data-testid={`quora-mark-${r.id}`}>
            <CheckCircle size={12}/> Mark posted
          </button>
        </div>
      )}
    </div>
  );
}
